import { useState, useEffect, useCallback } from 'react';
import { CheckCircle, XCircle, Edit3, ChevronDown, ChevronUp, Loader2, Send, CheckCheck, RefreshCw, ClipboardCheck } from 'lucide-react';
import PageHeader from '../../components/PageHeader';

interface DraftWithProspect {
  id: number;
  prospect_id: number;
  step: 1 | 2 | 3;
  subject: string;
  body: string;
  approved: number;
  edited: number;
  nombre: string;
  email: string;
  isla: string | null;
  municipio: string | null;
  tipo_fuente: string | null;
  web: string | null;
  priority: string | null;
  sequence_type: 'A' | 'B' | 'C';
  final_score: number | null;
  prospect_status: string;
}

interface GroupedProspect {
  prospect_id: number;
  nombre: string;
  email: string;
  isla: string | null;
  municipio: string | null;
  tipo_fuente: string | null;
  web: string | null;
  priority: string | null;
  sequence_type: 'A' | 'B' | 'C';
  final_score: number | null;
  prospect_status: string;
  drafts: DraftWithProspect[];
}

const SEQ_LABEL: Record<string, string> = { A: 'Web', B: 'Booking', C: 'Sin web' };
const SEQ_COLOR: Record<string, string> = {
  A: 'bg-blue-100 text-blue-700',
  B: 'bg-purple-100 text-purple-700',
  C: 'bg-orange-100 text-orange-700',
};

export default function OutreachReviewPage() {
  const [drafts, setDrafts] = useState<DraftWithProspect[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState<Record<number, boolean>>({});
  const [expanded, setExpanded] = useState<Record<number, boolean>>({});
  const [editing, setEditing] = useState<Record<number, { subject: string; body: string }>>({});
  const [page, setPage] = useState(1);
  const LIMIT = 30;

  const fetchDrafts = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`/api/outreach/drafts?approved=0&limit=${LIMIT}&page=${page}`);
      const data = await res.json() as { drafts: DraftWithProspect[]; total: number };
      setDrafts(data.drafts ?? []);
      setTotal(data.total ?? 0);
    } finally {
      setLoading(false);
    }
  }, [page]);

  useEffect(() => { fetchDrafts(); }, [fetchDrafts]);

  // Group drafts by prospect
  const grouped: GroupedProspect[] = drafts.reduce((acc, d) => {
    const existing = acc.find(g => g.prospect_id === d.prospect_id);
    if (existing) {
      existing.drafts.push(d);
    } else {
      acc.push({
        prospect_id: d.prospect_id,
        nombre: d.nombre,
        email: d.email,
        isla: d.isla,
        municipio: d.municipio,
        tipo_fuente: d.tipo_fuente,
        web: d.web,
        priority: d.priority,
        sequence_type: d.sequence_type,
        final_score: d.final_score,
        prospect_status: d.prospect_status,
        drafts: [d],
      });
    }
    return acc;
  }, [] as GroupedProspect[]);

  const toggleExpand = (id: number) => setExpanded(e => ({ ...e, [id]: !e[id] }));

  const startEdit = (draft: DraftWithProspect) => {
    setEditing(e => ({
      ...e,
      [draft.id]: { subject: draft.subject, body: draft.body },
    }));
  };

  const cancelEdit = (draftId: number) => {
    setEditing(e => { const c = { ...e }; delete c[draftId]; return c; });
  };

  const saveEdit = async (draftId: number) => {
    const ed = editing[draftId];
    if (!ed) return;
    setSaving(s => ({ ...s, [draftId]: true }));
    try {
      await fetch('/api/outreach/drafts', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: draftId, subject: ed.subject, body_text: ed.body }),
      });
      cancelEdit(draftId);
      await fetchDrafts();
    } finally {
      setSaving(s => ({ ...s, [draftId]: false }));
    }
  };

  const approveProspect = async (prospectId: number) => {
    setSaving(s => ({ ...s, [prospectId]: true }));
    try {
      await fetch('/api/outreach/drafts', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ approve_all: true, prospect_id: prospectId }),
      });
      await fetchDrafts();
    } finally {
      setSaving(s => ({ ...s, [prospectId]: false }));
    }
  };

  const rejectProspect = async (prospectId: number) => {
    setSaving(s => ({ ...s, [prospectId]: true }));
    try {
      await fetch('/api/outreach/prospects', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: prospectId, status: 'skipped' }),
      });
      await fetchDrafts();
    } finally {
      setSaving(s => ({ ...s, [prospectId]: false }));
    }
  };

  const approveAll = async () => {
    if (!confirm(`¿Aprobar todos los ${grouped.length} prospects en esta página?`)) return;
    for (const g of grouped) {
      await approveProspect(g.prospect_id);
    }
  };

  const totalPages = Math.ceil(total / LIMIT);

  return (
    <div className="max-w-4xl mx-auto">
      <PageHeader
        type="outreach"
        icon={ClipboardCheck}
        title="Revisar Emails"
        subtitle={`${total} borradores esperando aprobación`}
        actions={
          <div className="flex gap-2">
            <button
              onClick={fetchDrafts}
              className="btn-secondary flex items-center gap-1.5"
            >
              <RefreshCw size={14} /> Actualizar
            </button>
            {grouped.length > 0 && (
              <button
                onClick={approveAll}
                className="btn-primary flex items-center gap-1.5"
              >
                <CheckCheck size={14} /> Aprobar todos ({grouped.length})
              </button>
            )}
          </div>
        }
      />

      {loading ? (
        <div className="flex justify-center py-16">
          <Loader2 size={24} className="animate-spin text-teal-500" />
        </div>
      ) : grouped.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
          <Send size={32} className="mx-auto text-gray-300 mb-3" />
          <p className="text-gray-500 font-medium">No hay borradores pendientes</p>
          <p className="text-sm text-gray-400 mt-1">
            Ejecuta <code className="bg-gray-100 px-1.5 py-0.5 rounded text-xs">npx tsx scripts/outreach/outreach-prepare.ts</code> para generar nuevos emails
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {grouped.map(g => (
            <div key={g.prospect_id} className="bg-white rounded-xl border border-gray-200 overflow-hidden">
              {/* Prospect header */}
              <div className="flex items-center gap-3 p-4 border-b border-gray-100">
                <button
                  onClick={() => toggleExpand(g.prospect_id)}
                  className="flex items-center gap-3 flex-1 min-w-0 text-left"
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold text-gray-900 truncate">{g.nombre}</span>
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${SEQ_COLOR[g.sequence_type]}`}>
                        Seq {g.sequence_type} · {SEQ_LABEL[g.sequence_type]}
                      </span>
                      {g.priority && (
                        <span className="text-xs text-gray-400">P{g.priority}</span>
                      )}
                    </div>
                    <div className="text-sm text-gray-500 flex gap-2 mt-0.5 flex-wrap">
                      <span>{g.email}</span>
                      {g.isla && <span>· {g.municipio ?? g.isla}</span>}
                      {g.tipo_fuente && <span>· {g.tipo_fuente}</span>}
                      {g.web && <a href={g.web} target="_blank" rel="noopener" className="text-teal-600 hover:underline" onClick={e => e.stopPropagation()}>· {g.web.replace(/^https?:\/\//, '')}</a>}
                    </div>
                  </div>
                  {expanded[g.prospect_id] ? <ChevronUp size={16} className="text-gray-400 shrink-0" /> : <ChevronDown size={16} className="text-gray-400 shrink-0" />}
                </button>

                <div className="flex gap-2 shrink-0">
                  <button
                    onClick={() => rejectProspect(g.prospect_id)}
                    disabled={saving[g.prospect_id]}
                    className="p-1.5 rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors disabled:opacity-40"
                    title="Saltar / rechazar"
                  >
                    <XCircle size={18} />
                  </button>
                  <button
                    onClick={() => approveProspect(g.prospect_id)}
                    disabled={saving[g.prospect_id]}
                    className="p-1.5 rounded-lg text-gray-400 hover:text-green-600 hover:bg-green-50 transition-colors disabled:opacity-40"
                    title="Aprobar todos los pasos"
                  >
                    {saving[g.prospect_id]
                      ? <Loader2 size={18} className="animate-spin" />
                      : <CheckCircle size={18} />}
                  </button>
                </div>
              </div>

              {/* Expanded drafts */}
              {expanded[g.prospect_id] && (
                <div className="divide-y divide-gray-50">
                  {g.drafts.sort((a, b) => a.step - b.step).map(draft => {
                    const ed = editing[draft.id];
                    return (
                      <div key={draft.id} className="p-4">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                            Paso {draft.step}
                          </span>
                          {!ed && (
                            <button
                              onClick={() => startEdit(draft)}
                              className="text-xs text-teal-600 hover:underline flex items-center gap-1"
                            >
                              <Edit3 size={11} /> Editar
                            </button>
                          )}
                        </div>

                        {ed ? (
                          <div className="space-y-2">
                            <input
                              value={ed.subject}
                              onChange={e => setEditing(prev => ({
                                ...prev,
                                [draft.id]: { ...prev[draft.id], subject: e.target.value },
                              } as Record<number, { subject: string; body: string }>))}
                              className="w-full border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                              placeholder="Asunto"
                            />
                            <textarea
                              value={ed.body}
                              onChange={e => setEditing(prev => ({
                                ...prev,
                                [draft.id]: { ...prev[draft.id], body: e.target.value },
                              } as Record<number, { subject: string; body: string }>))}
                              rows={10}
                              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-teal-500 resize-y"
                            />
                            <div className="flex gap-2 justify-end">
                              <button
                                onClick={() => cancelEdit(draft.id)}
                                className="btn-secondary text-sm px-3 py-1.5"
                              >
                                Cancelar
                              </button>
                              <button
                                onClick={() => saveEdit(draft.id)}
                                disabled={saving[draft.id]}
                                className="btn-primary text-sm px-3 py-1.5 flex items-center gap-1.5"
                              >
                                {saving[draft.id] ? <Loader2 size={12} className="animate-spin" /> : null}
                                Guardar
                              </button>
                            </div>
                          </div>
                        ) : (
                          <>
                            <p className="text-sm font-medium text-gray-700 mb-1">
                              Asunto: <span className="font-normal text-gray-600">{draft.subject}</span>
                            </p>
                            <pre className="text-sm text-gray-600 whitespace-pre-wrap font-sans leading-relaxed bg-gray-50 rounded-lg p-3">
                              {draft.body}
                            </pre>
                          </>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          ))}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex justify-center gap-2 pt-2">
              <button
                disabled={page <= 1}
                onClick={() => setPage(p => p - 1)}
                className="btn-secondary px-4 py-2 text-sm disabled:opacity-40"
              >
                ← Anterior
              </button>
              <span className="py-2 px-3 text-sm text-gray-500">{page} / {totalPages}</span>
              <button
                disabled={page >= totalPages}
                onClick={() => setPage(p => p + 1)}
                className="btn-secondary px-4 py-2 text-sm disabled:opacity-40"
              >
                Siguiente →
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
